import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LoginModal from "@/components/LoginModal";
import ApplicationModal from "@/components/ApplicationModal";
import { HeroLogoEffect } from "@/components/effects/HeroLogoEffect";
import { TAB_STYLES } from "@/components/TabStyles";
import { FONTS } from "@/components/fonts";
import { useAuth } from "@/context/AuthContext";

const LOCATION_STYLES = [
  { id: "rules", name: "Rule marks" },
  { id: "dots", name: "Dot separators" },
  { id: "brackets", name: "Brackets" },
  { id: "coords", name: "Coordinates" },
  { id: "wide", name: "Wide-tracked" },
  { id: "serif", name: "Serif italic" },
];

function LocationTag({ styleId }) {
  const base = "flex items-center justify-center gap-3 text-neutral-500";

  if (styleId === "dots") {
    return (
      <div className={`${base} text-[10px] uppercase tracking-[0.4em]`}>
        <span className="w-1 h-1 rounded-full bg-neutral-400" aria-hidden />
        <span>Los Angeles</span>
        <span className="w-1 h-1 rounded-full bg-neutral-400" aria-hidden />
      </div>
    );
  }

  if (styleId === "brackets") {
    return (
      <div className={`${base} text-[10px] uppercase tracking-[0.35em]`}>
        <span aria-hidden>[</span>
        <span>Los Angeles</span>
        <span aria-hidden>]</span>
      </div>
    );
  }

  if (styleId === "coords") {
    return (
      <div className={`${base} text-[10px] uppercase tracking-[0.3em] font-mono`}>
        <span>Los Angeles</span>
        <span className="text-neutral-300" aria-hidden>·</span>
        <span>34.05°N 118.24°W</span>
      </div>
    );
  }

  if (styleId === "wide") {
    return (
      <div className={`${base} text-[10px] uppercase`} style={{ letterSpacing: "0.75em", paddingLeft: "0.75em" }}>
        <span>Los Angeles</span>
      </div>
    );
  }

  if (styleId === "serif") {
    return (
      <div className={`${base} text-base italic`} style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
        <span>Los Angeles</span>
      </div>
    );
  }

  // default: rules
  return (
    <div className={`${base} text-[10px] uppercase tracking-[0.4em]`}>
      <span className="inline-block w-8 h-px bg-neutral-400" aria-hidden />
      <span>Los Angeles</span>
      <span className="inline-block w-8 h-px bg-neutral-400" aria-hidden />
    </div>
  );
}

export default function Landing() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [effect] = useState("typewriter");
  const [tabStyle] = useState("arrow");
  const [fontId] = useState("manrope");
  const [locationStyle] = useState("wide");
  const { user, logout } = useAuth();

  // Apply font globally via CSS variable
  useEffect(() => {
    const f = FONTS.find((x) => x.id === fontId) ?? FONTS[0];
    document.documentElement.style.setProperty("--wn-font", f.family);
  }, [fontId]);

  const openModal = (mode) => {
    if (mode === "signup" || mode === "apply") {
      setApplyOpen(true);
    } else {
      setLoginOpen(true);
    }
  };

  const TabsComp = (TAB_STYLES.find((t) => t.id === tabStyle) ?? TAB_STYLES[0]).Comp;

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen w-full text-neutral-300 flex flex-col relative overflow-hidden"
      id="top"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, #2a2a2a 0%, #141414 40%, #050505 80%, #000000 100%)",
      }}
    >
      <AnimatePresence>
        {user && user !== false && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            className="absolute top-0 right-0 p-6 z-30 flex items-center gap-4 text-[10px] uppercase tracking-[0.25em]"
          >
            <span data-testid="user-email" className="text-neutral-400">
              {user.email}
            </span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="border border-neutral-500 text-neutral-300 px-4 py-2 hover:bg-neutral-200 hover:text-black transition-colors"
            >
              Sign Out
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-3xl">
          <h1
            key={effect}
            data-testid="hero-wordmark"
            aria-label="Wellnet"
            className="block w-full"
          >
            <HeroLogoEffect effectId={effect} />
          </h1>

          <div className="mt-14" data-testid={`tabs-style-${tabStyle}`}>
            <TabsComp
              onLogin={() => openModal("login")}
              onApply={() => openModal("signup")}
            />
          </div>
        </div>
      </main>

      <footer
        data-testid="site-footer"
        className="w-full px-6 pb-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] uppercase tracking-[0.3em] text-neutral-500"
      >
        <div data-testid="footer-copyright">© 2026 Wellnet</div>
        <div className="flex gap-6">
          <a href="/support" data-testid="footer-support-link" className="hover:text-neutral-300 transition-colors">Support</a>
          <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-neutral-300 transition-colors">Privacy</a>
        </div>
      </footer>

      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        initialMode="login"
      />
      <ApplicationModal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
      />
    </div>
  );
}
